'use client';

import { MouseEvent, useEffect, useState } from 'react';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';

// Services
import { scrollIntoView } from '@/services/navigation/scroll';

const retrieveCurrentHash = () =>
  typeof window !== 'undefined' ? window.location.hash : undefined;

export const useContentNavigation = () => {
  const router = useRouter();
  const path = usePathname();
  const searchParams = useSearchParams();

  const [isClient, setIsClient] = useState(false);
  const [hash, setHash] = useState<string | undefined>(retrieveCurrentHash());

  useEffect(() => {
    const onHashChanged = () => setHash(window.location.hash);
    const { pushState, replaceState } = window.history;

    window.history.pushState = (...args) => {
      pushState.apply(window.history, args);
      setTimeout(() => setHash(window.location.hash));
    };

    window.history.replaceState = (...args) => {
      replaceState.apply(window.history, args);
      setTimeout(() => setHash(window.location.hash));
    };

    const currentHash = retrieveCurrentHash();
    setHash(currentHash);

    if (currentHash) {
      scrollIntoView(currentHash.replace('#', ''));
    }

    setIsClient(true);

    window.addEventListener('hashchange', onHashChanged);

    return () => {
      window.removeEventListener('hashchange', onHashChanged);
    };
  }, []);

  const onNavigationUpdate = (
    event: MouseEvent<HTMLAnchorElement>,
    id: string,
  ) => {
    event.preventDefault();

    const formatHash = `#${id}`;

    setHash(formatHash);
    router.replace(formatHash);
  };

  const removeHash = () => {
    let newPath = path ?? '';

    if (searchParams) {
      newPath += `?${searchParams.toString()}`;
    }

    router.replace(newPath, { scroll: false });
  };

  return {
    removeHash: removeHash,
    hash: isClient ? hash : null,
    onNavigationUpdate: onNavigationUpdate,
  };
};
